export const profile = {
  name: 'Yolandi Jardim',
  title: 'Director & Enterprise Leader | Analytics, Data & AI Governance',
  valueStatement:
    'Pioneering Board-endorsed AI & data strategies, enterprise operating models, and regulatory assurance across financial services, energy and consulting.',
  location: 'Melbourne, Victoria',
  seniority: 'Senior Enterprise Executive · 20+ Years Experience',
  quote: 'Building trusted data & responsible AI in regulated environments.',
  portrait:
    'https://cdn.magicpatterns.com/uploads/kkmfSMarnmiVS2imoKQZj3/Yolandi-Picsart-AiImageEnhancer.jpg',
  email: 'yolandi.jardim@gmail.com',
  phone: '0435 854 339',
  linkedIn: 'https://www.linkedin.com/in/yolandi-jardim',
  disclaimer:
    'Available for Executive Leadership, Board Advisory, and Non-Executive Director (NED) consultation.',
}

/* ── 01 About ───────────────────────────────────────────────── */

export const about = {
  biography: [
    'Yolandi Jardim is an enterprise executive with more than twenty years spent shaping how large, regulated organisations govern their analytics, data and artificial intelligence. She leads at the intersection of strategy, risk and technology — the place where obligation has to be turned into an operating capability that survives beyond a program.',
    'She is trusted by boards, executive committees and regulators to make the consequences of data and AI risk unambiguous, and to say plainly what an organisation can and cannot responsibly do next. Her approach is deliberately unglamorous: clear accountability, evidence that holds up under scrutiny, and capability left behind in the business rather than held in a central team.',
    'Originally trained in the sciences and forensic analytics, she brings an investigator’s discipline to governance — assume nothing, evidence everything — paired with the commercial pragmatism required to keep transformation funded and moving.',
  ],
  philosophy: [
    {
      title: 'Accountability sits where data is created',
      text: 'Central teams can set the standard, but ownership only holds when the business domain that creates the data also answers for it.',
    },
    {
      title: 'Governance must enable, not obstruct',
      text: 'Controls exist so an organisation can move faster with confidence. If a control only ever says no, it has been designed badly.',
    },
    {
      title: 'Evidence over assertion',
      text: 'A board is entitled to see the artefact, not a summary of a summary. Assurance is built from things that can be inspected.',
    },
  ],
  glance: [
    { label: 'Executive experience', value: '20+ years' },
    { label: 'Based in', value: 'Melbourne, Victoria' },
    { label: 'Current mandate', value: 'Head of Data & AI Governance' },
    { label: 'Operating focus', value: 'Regulated enterprise' },
  ],
}

/* ── 02 Experiences ─────────────────────────────────────────── */

export type IndustryExperience = {
  sector: string
  years: string
  focus: string
  exposure: string[]
}

export const industryExperience: IndustryExperience[] = [
  {
    sector: 'Energy & Critical Infrastructure',
    years: '2022 – Present',
    focus:
      'Governing data and AI for an essential-services operator where availability, safety and public trust are non-negotiable.',
    exposure: ['Asset-intensive data domains', 'Safety-critical decisioning', 'Essential services obligations'],
  },
  {
    sector: 'Banking & Financial Services',
    years: '2019 – 2022',
    focus:
      'Enterprise data risk within a major Australian bank operating under sustained prudential supervision.',
    exposure: ['Prudential data risk', 'Enterprise lineage', 'Regulated reporting domains'],
  },
  {
    sector: 'Professional Services & Consulting',
    years: '2018 – 2019 · Earlier',
    focus:
      'Advisory to public sector and enterprise clients on data assurance, risk analytics and forensic investigation.',
    exposure: ['Client advisory', 'Practice building', 'Public sector engagement'],
  },
  {
    sector: 'Insurance & Health',
    years: '2015 – 2018',
    focus:
      'Analytics leadership across life insurance and private health, including separation and standalone capability build.',
    exposure: ['Customer analytics', 'Divestment separation', 'Member and policyholder data'],
  },
]

export const regulatoryEnvironments = [
  {
    name: 'APRA',
    detail: 'Prudential data risk supervision, CRDE100 obligations and Data Management Plan artefacts.',
  },
  {
    name: 'FIRB',
    detail: 'Foreign investment conditions and audit evidence across legal, risk and technology functions.',
  },
  {
    name: 'Australian Privacy Principles',
    detail: 'Privacy audit response, remediation planning and closure of findings within committed timeframes.',
  },
  {
    name: 'Victorian Government (VGRMF)',
    detail: 'Public sector risk management framework contribution and adoption of published guidance.',
  },
]

export const engagementTypes = [
  'Board and executive committee reporting',
  'Regulator and external audit engagement',
  'Multi-year transformation portfolios',
  'Operating model design and stand-up',
  'Practice and capability establishment',
  'Post-divestment separation programs',
]

/* ── 03 Skills ──────────────────────────────────────────────── */

export type SkillGroup = {
  title: string
  intent: string
  skills: { name: string; level: number; note: string }[]
}

export const skillGroups: SkillGroup[] = [
  {
    title: 'Executive Leadership',
    intent: 'Setting direction and holding the line on it.',
    skills: [
      { name: 'Enterprise strategy definition', level: 95, note: 'Board-endorsed strategy and roadmap ownership' },
      { name: 'Executive decision framing', level: 95, note: 'Reducing complex risk to a decidable choice' },
      { name: 'Portfolio prioritisation', level: 88, note: 'Sequencing multi-year investment under constraint' },
    ],
  },
  {
    title: 'Influence & Communication',
    intent: 'Being understood by directors, regulators and engineers alike.',
    skills: [
      { name: 'Board and committee reporting', level: 95, note: 'Papers written to be acted on, not filed' },
      { name: 'Regulator engagement', level: 90, note: 'Direct, evidenced, no surprises' },
      { name: 'Stakeholder negotiation', level: 88, note: 'Agreements across competing enterprise interests' },
    ],
  },
  {
    title: 'Change & Delivery',
    intent: 'Turning intent into an operating reality.',
    skills: [
      { name: 'Enterprise change leadership', level: 92, note: 'Adoption across thousands of staff' },
      { name: 'Operating model design', level: 92, note: 'Federated accountability that holds' },
      { name: 'Commercial management', level: 85, note: 'Funding, benefits and value realisation' },
    ],
  },
  {
    title: 'People & Capability',
    intent: 'Leaving the organisation more capable than it was found.',
    skills: [
      { name: 'Building and leading teams', level: 92, note: 'Analytics, governance and data science functions' },
      { name: 'Capability uplift & training', level: 90, note: 'Structured enterprise learning programs' },
      { name: 'Coaching & mentoring', level: 88, note: 'Developing senior data leaders' },
    ],
  },
]

/* ── 05 Impact & Capital Metrics ────────────────────────────── */

export type Metric = {
  value: string
  label: string
  detail: string
}

export const metrics: Metric[] = [
  {
    value: '$50M+',
    label: 'Enterprise data & lineage transformation led',
    detail: 'Multi-year uplift delivered against APRA CRDE100 obligations.',
  },
  {
    value: '320+',
    label: 'Data assets under enterprise ownership',
    detail: 'Ownership framework established across six business domains.',
  },
  {
    value: '100%',
    label: 'Privacy audit remediation closure',
    detail: 'Australian Privacy Principles findings closed within twelve months.',
  },
  {
    value: 'Clean',
    label: 'Consecutive FIRB audit outcomes',
    detail: 'Two consecutive clean results across legal, risk and technology.',
  },
]

export const capitalMetrics: Metric[] = [
  {
    value: '$2M+',
    label: 'First-year revenue generated',
    detail: 'Risk analytics service line built and commercialised.',
  },
  {
    value: '700+',
    label: 'Critical data elements catalogued',
    detail: 'Framework applied across the bank’s key reporting domains.',
  },
  {
    value: '~1,000',
    label: 'Staff reached by AI capability program',
    detail: 'Five-module enterprise training curriculum delivered.',
  },
  {
    value: '10',
    label: 'Regulatory artefacts authored',
    detail: 'APRA Data Management Plan documents with positive feedback.',
  },
  {
    value: '6',
    label: 'Business domains federated',
    detail: 'Domain ownership operating model stood up end to end.',
  },
  {
    value: '400+',
    label: 'Assets in scope of data uplift',
    detail: 'Quality and lineage remediation across the enterprise estate.',
  },
]

/* ── 06 Value Proposition & Board Competencies ──────────────── */

export const positioning =
  'Translating complex data and AI risks into board-level decisions, enabling safe and scalable AI adoption, and embedding business-owned accountability.'

export type Pillar = {
  number: string
  title: string
  description: string
  points: string[]
}

export const pillars: Pillar[] = [
  {
    number: '01',
    title: 'Strategic AI & Data Governance',
    description:
      'Board-approved strategy, GenAI guardrails and off-by-default controls that let organisations adopt AI without absorbing uncontrolled risk.',
    points: ['Board and council mandates', 'AI registers and use-case triage', 'Responsible AI standards'],
  },
  {
    number: '02',
    title: 'Regulatory Assurance & Audit Leadership',
    description:
      'Regulatory programs delivered end to end, with evidence, artefacts and remediation that regulators and auditors accept.',
    points: ['APRA obligations', 'FIRB audit readiness', 'Australian Privacy Principles'],
  },
  {
    number: '03',
    title: 'Federated Operating Models & Capability',
    description:
      'Data Mesh operating models, domain ownership and internal capability uplift so governance survives beyond the program.',
    points: ['Data Mesh and domain ownership', 'Data Science Hubs', 'DAMA-aligned standards'],
  },
  {
    number: '04',
    title: 'Commercial & Value Realisation',
    description:
      'Analytics investment tied to measurable commercial outcomes — revenue growth, cost efficiency and scalable delivery.',
    points: ['Revenue generation', 'Cost-to-serve efficiency', 'Scalable analytics platforms'],
  },
]

export const boardCompetencies = [
  {
    name: 'Technology & Digital Oversight',
    detail: 'Independent challenge of technology strategy, platform investment and delivery assurance.',
  },
  {
    name: 'Risk & Compliance',
    detail: 'Data, privacy and AI risk appetite, control design and remediation oversight.',
  },
  {
    name: 'Audit Readiness',
    detail: 'Evidence standards and artefact quality that stand up to regulatory and external audit.',
  },
  {
    name: 'Strategy & Transformation',
    detail: 'Multi-year change portfolios governed against measurable benefit.',
  },
  {
    name: 'Ethics & Responsible AI',
    detail: 'Ethical frameworks for automated decisioning and generative capability.',
  },
  {
    name: 'Stakeholder & Regulator Relations',
    detail: 'Constructive, evidenced engagement with supervisory bodies.',
  },
]

/* ── 07 Career Track Record ─────────────────────────────────── */

export type Role = {
  title: string
  organisation: string
  period: string
  mandate: string
  achievements: string[]
}

export type EarlierRole = {
  organisation: string
  title: string
  period: string
  context: string
}

export const roles: Role[] = [
  {
    title: 'Head of Data & AI Governance',
    organisation: 'Jemena',
    period: 'May 2022 – Present',
    mandate:
      'Accountable for the enterprise data and AI governance agenda across a critical energy infrastructure operator.',
    achievements: [
      "Defined Jemena's first Board-approved Data & AI Strategy and supporting 10-point roadmap.",
      'Operationalised the Data Governance Council and a federated Data Mesh architecture on Databricks (AWS).',
      'Shaped GenAI governance, AI registers and off-by-default controls for emerging AI capability.',
      'Spearheaded enterprise AI Week and a five-module training program reaching approximately 1,000 staff.',
    ],
  },
  {
    title: 'Head of Data Quality & Lineage',
    organisation: 'National Australia Bank (NAB)',
    period: 'Oct 2019 – May 2022',
    mandate:
      'Led the enterprise data quality and lineage uplift underpinning the bank’s prudential data risk obligations.',
    achievements: [
      'Directed a ~$50M enterprise data uplift spanning 400+ data assets for APRA CRDE100.',
      'Designed the Critical Data Element framework, cataloguing 700+ elements across key domains.',
      'Authored approximately 10 APRA Data Management Plan artefacts, receiving positive regulatory feedback.',
    ],
  },
  {
    title: 'Director, Data Assurance & Risk Analytics',
    organisation: 'PwC Australia',
    period: 'Jul 2018 – Oct 2019',
    mandate: 'Built and led a risk analytics practice serving public sector and enterprise clients.',
    achievements: [
      'Generated $2M+ first-year revenue in risk analytics services.',
      'Authored a technical paper adopted into the Victorian Government Risk Management Framework (VGRMF).',
    ],
  },
  {
    title: 'Head of Data Analytics',
    organisation: 'MLC Insurance',
    period: 'Feb 2017 – Jun 2018',
    mandate: 'Established standalone analytics capability through a complex post-divestment separation.',
    achievements: [
      'Built the post-divestment Data Science Hub from the ground up.',
      'Negotiated critical data access agreements with NAB to protect continuity of analytics.',
      'Delivered Single Customer View data assets supporting retention and servicing.',
    ],
  },
]

export const earlierRoles: EarlierRole[] = [
  {
    organisation: 'Medibank',
    title: 'Senior Manager, Analytics',
    period: 'Earlier',
    context: 'Analytics leadership across private health insurance products and members.',
  },
  {
    organisation: 'EY Australia & South Africa',
    title: 'Senior Manager, Forensic Data Analytics',
    period: 'Earlier',
    context: 'Cross-border forensic investigation and investigative analytics engagements.',
  },
  {
    organisation: 'Easirun Software',
    title: 'Data & Software Delivery',
    period: 'Earlier',
    context: 'Enterprise software and data engineering foundations.',
  },
]

/* ── 08 Technical Mastery & Systems Portfolio ───────────────── */

export type CapabilityGroup = {
  title: string
  description: string
  items: string[]
}

export const capabilityGroups: CapabilityGroup[] = [
  {
    title: 'Frameworks & Standards',
    description: 'The reference models governance programs are designed and measured against.',
    items: ['DAMA-DMBOK', 'DCAM', 'COBIT', 'CMMI', 'CRISP-DM', 'Australian Privacy Principles'],
  },
  {
    title: 'Governance & Catalogue Tooling',
    description: 'Platforms used to operationalise catalogue, lineage and ownership at scale.',
    items: ['Databricks Unity Catalog', 'Data Mesh Manager', 'Informatica', 'Collibra', 'Alation'],
  },
  {
    title: 'Data & Analytics Engineering',
    description: 'The technical foundations she has worked in directly and governed as an executive.',
    items: ['Databricks on AWS', 'SQL', 'SAS', 'Data modelling', 'Lineage instrumentation', 'Data visualisation'],
  },
]

export const systemsPortfolio = [
  {
    layer: 'Domain ownership',
    detail: 'Six federated business domains, each with named accountable owners and published data products.',
  },
  {
    layer: 'Catalogue & lineage',
    detail: 'Unity Catalog and Data Mesh Manager providing discoverability, lineage and policy enforcement.',
  },
  {
    layer: 'Platform',
    detail: 'Databricks lakehouse on AWS as the governed analytics and AI execution environment.',
  },
  {
    layer: 'AI control plane',
    detail: 'AI registers, use-case triage and off-by-default guardrails governing generative capability.',
  },
]

/* ── 09 Board Experience, Education & Civic Leadership ──────── */

export const boardService = [
  {
    body: 'Data Governance Council',
    role: 'Founder & Chair',
    period: '2022 – Present',
    detail:
      'Established and chairs the enterprise forum that sets data and AI policy and adjudicates domain accountability.',
  },
  {
    body: 'Executive Risk & Technology Forums',
    role: 'Standing contributor',
    period: '2019 – Present',
    detail:
      'Regular reporting line into executive risk and technology committees on data, privacy and AI exposure.',
  },
  {
    body: 'AI Governance & Ethics Working Group',
    role: 'Sponsor',
    period: '2023 – Present',
    detail:
      'Sponsors the cross-functional group reviewing generative AI use cases against ethical and regulatory standards.',
  },
]

export const education = {
  degree: 'Bachelor of Science (BSc)',
  degreeDetail: 'Foundation in quantitative and analytical disciplines.',
  certifications: [
    { name: 'Investigative & Forensic Analytics', detail: 'Advanced certification' },
    { name: 'Data Visualisation', detail: 'Advanced certification' },
    { name: 'SAS / SQL Data Engineering', detail: 'Advanced certification' },
  ],
}

export const civicLeadership = [
  {
    title: 'Industry speaking & thought leadership',
    detail:
      'Speaks publicly on responsible AI adoption and data governance practice in regulated industries.',
  },
  {
    title: 'Mentoring emerging data leaders',
    detail:
      'Mentors senior analytics and governance practitioners moving into their first executive appointments.',
  },
  {
    title: 'Advancing women in data & technology',
    detail:
      'Advocates for representation of women in enterprise data, analytics and AI leadership roles.',
  },
]

/* ── 10 Contact ─────────────────────────────────────────────── */

export const inquiryTypes = [
  'Non-Executive Director appointment',
  'Board advisory or committee role',
  'Executive leadership opportunity',
  'Speaking or panel invitation',
  'Other enquiry',
]
